// O envio do vídeo de um episódio, do navegador do Mentor (Círculo, P3).
//
// Contrato (server/circulo/routes.js + video.js): POST .../upload abre o envio
// → PUT .../upload/:id?offset=N com pedaços de até 8 MB → POST
// .../upload/concluido. O servidor guarda o que chegou: se a rede cair, o
// envio pergunta o offset e continua; se a aba fechar, a tela mostra o envio
// pela metade (GET /estudio traz nome, tamanho e data do arquivo) e, quando o
// Mentor escolhe o MESMO arquivo de novo, continua daquele ponto.
//
// Um envio por vez, guardado aqui no módulo (e não na tela): andar pelo app
// não interrompe. Fechar a aba sim — por isso o aviso do beforeunload.

export type FaseEnvio = 'preparando' | 'enviando' | 'reconectando' | 'concluindo' | 'concluido' | 'erro' | 'cancelado';

export interface EstadoEnvio {
  epId: string;
  nome: string;
  tamanho: number;
  enviados: number;
  velocidade: number;      // bytes por segundo (média dos últimos segundos)
  fase: FaseEnvio;
  retomadoDe: number;      // bytes que já estavam no servidor ao começar (0 = do zero)
  erro?: string;
}

export interface EnvioPendente { upload_id: string; offset: number; tamanho: number; nome: string | null; modificado_em: number | null }

const ATIVAS: FaseEnvio[] = ['preparando', 'enviando', 'reconectando', 'concluindo'];
const JANELA_MS = 8000; // a velocidade é a média destes últimos segundos

let atual: EstadoEnvio | null = null;
let ctl: { cancelado: boolean; xhr: XMLHttpRequest | null; acordar: (() => void) | null } | null = null;
const ouvintes = new Set<(e: EstadoEnvio | null) => void>();

const avisar = () => { ouvintes.forEach((f) => f(atual ? { ...atual } : null)); };
const mudar = (m: Partial<EstadoEnvio>) => { if (atual) { atual = { ...atual, ...m }; avisar(); } };

export const envioAtivo = (e: EstadoEnvio | null) => !!e && ATIVAS.includes(e.fase);
export function ouvirEnvio(f: (e: EstadoEnvio | null) => void): () => void {
  ouvintes.add(f);
  f(atual ? { ...atual } : null);
  return () => { ouvintes.delete(f); };
}

// Fechar a aba no meio do envio: o navegador pergunta antes.
const segurarAba = (e: BeforeUnloadEvent) => { if (envioAtivo(atual)) { e.preventDefault(); e.returnValue = ''; } };

// O mesmo arquivo do envio pela metade? Nome, tamanho e data de modificação no PC.
export function mesmoArquivo(p: EnvioPendente | null | undefined, f: File): boolean {
  return !!p && p.nome === f.name && p.tamanho === f.size && (!p.modificado_em || p.modificado_em === f.lastModified);
}

class ErroDeRede extends Error {}
class Cancelado extends Error {}

export function cancelarEnvio() {
  if (!ctl || !atual || !envioAtivo(atual)) return;
  ctl.cancelado = true;
  ctl.xhr?.abort();
  ctl.acordar?.();
}

export async function enviarVideo(opts: {
  slug: string; epId: string; arquivo: File; pendente?: EnvioPendente | null; token: () => string | null | undefined;
}): Promise<boolean> {
  if (envioAtivo(atual)) throw new Error('Já há um vídeo sendo enviado. Espere terminar.');
  const { slug, epId, arquivo, token } = opts;
  const base = `/api/mentores/${encodeURIComponent(slug)}/episodios/${epId}/upload`;
  const meu = { cancelado: false, xhr: null as XMLHttpRequest | null, acordar: null as (() => void) | null };
  ctl = meu;
  atual = { epId, nome: arquivo.name, tamanho: arquivo.size, enviados: 0, velocidade: 0, fase: 'preparando', retomadoDe: 0 };
  avisar();
  window.addEventListener('beforeunload', segurarAba);

  const amostras: [number, number][] = [];
  const medir = (bytes: number) => {
    const t = Date.now();
    amostras.push([t, bytes]);
    while (amostras.length > 2 && t - amostras[0][0] > JANELA_MS) amostras.shift();
    const [t0, b0] = amostras[0];
    const vel = t - t0 > 400 ? Math.max(0, (bytes - b0) / ((t - t0) / 1000)) : atual?.velocidade || 0;
    mudar({ enviados: bytes, velocidade: vel });
  };

  const checar = () => { if (meu.cancelado) throw new Cancelado(); };
  // Espera (ou até a rede voltar, ou o cancelamento).
  const pausa = (ms: number) => new Promise<void>((r) => {
    const fim = () => { clearTimeout(t); window.removeEventListener('online', fim); meu.acordar = null; r(); };
    const t = setTimeout(fim, ms);
    window.addEventListener('online', fim);
    meu.acordar = fim;
  });

  async function pedir(metodo: string, url: string, corpo?: any): Promise<{ status: number; j: any }> {
    let r: Response;
    try {
      r = await fetch(url, {
        method: metodo,
        headers: { Authorization: `Bearer ${token() || ''}`, ...(corpo ? { 'Content-Type': 'application/json' } : {}) },
        body: corpo ? JSON.stringify(corpo) : undefined,
        cache: 'no-store',
      });
    } catch { throw new ErroDeRede(); }
    let j: any = {};
    try { j = await r.json(); } catch { /* sem corpo */ }
    return { status: r.status, j };
  }

  function pedaco(url: string, blob: Blob, inicio: number): Promise<{ status: number; j: any }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      meu.xhr = xhr;
      xhr.open('PUT', url);
      xhr.setRequestHeader('Authorization', `Bearer ${token() || ''}`);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.upload.onprogress = (e) => medir(inicio + e.loaded);
      xhr.onload = () => {
        let j: any = {};
        try { j = JSON.parse(xhr.responseText || '{}'); } catch { /* sem corpo */ }
        resolve({ status: xhr.status, j });
      };
      xhr.onerror = () => reject(new ErroDeRede());
      xhr.ontimeout = () => reject(new ErroDeRede());
      xhr.onabort = () => reject(meu.cancelado ? new Cancelado() : new ErroDeRede());
      xhr.send(blob);
    });
  }

  // Erro de rede e servidor fora do ar: espera e tenta de novo, para sempre
  // (até o cancelamento). 401 = o login está sendo renovado pelo app.
  let falhas = 0;
  const transitorio = (s: number) => s === 401 || s === 408 || s === 429 || s >= 500;
  async function respirar() {
    falhas++;
    mudar({ fase: 'reconectando', velocidade: 0 });
    await pausa(Math.min(30000, 1000 * 2 ** Math.min(falhas, 5)));
    checar();
  }

  let uploadId = '';
  let offset = 0;
  let recomecos = 0;

  async function abrirNovo() {
    for (;;) {
      checar();
      let r;
      try { r = await pedir('POST', base, { tamanho: arquivo.size, tipo: arquivo.type, nome_arquivo: arquivo.name, modificado_em: arquivo.lastModified }); } catch (e) {
        if (e instanceof ErroDeRede) { await respirar(); continue; }
        throw e;
      }
      if (r.status === 201) { uploadId = r.j.upload_id; offset = 0; return; }
      if (transitorio(r.status)) { await respirar(); continue; }
      throw new Error(r.j?.error || `Não deu para começar o envio (${r.status}).`);
    }
  }

  // Onde o servidor parou; null = o envio não existe mais.
  async function perguntarOffset(): Promise<number | null> {
    for (;;) {
      checar();
      let r;
      try { r = await pedir('GET', `${base}/${uploadId}`); } catch (e) {
        if (e instanceof ErroDeRede) { await respirar(); continue; }
        throw e;
      }
      if (r.status === 200) return Number(r.j.offset) || 0;
      if (r.status === 404) return null;
      if (transitorio(r.status)) { await respirar(); continue; }
      throw new Error(r.j?.error || `Não deu para retomar o envio (${r.status}).`);
    }
  }

  try {
    const p = opts.pendente;
    if (mesmoArquivo(p, arquivo)) {
      uploadId = p!.upload_id;
      const o = await perguntarOffset();
      if (o == null) await abrirNovo(); else offset = o;
    } else {
      // Um envio pela metade de OUTRO arquivo: sai, para não ocupar disco.
      if (p) await pedir('DELETE', `${base}/${p.upload_id}`).catch(() => {});
      await abrirNovo();
    }
    mudar({ retomadoDe: offset, enviados: offset, fase: 'enviando' });
    amostras.length = 0;

    const PEDACO = 8 * 1024 * 1024;
    while (offset < arquivo.size) {
      checar();
      let r;
      try {
        r = await pedaco(`${base}/${uploadId}?offset=${offset}`, arquivo.slice(offset, Math.min(offset + PEDACO, arquivo.size)), offset);
      } catch (e) {
        if (e instanceof ErroDeRede) {
          await respirar();
          const o = await perguntarOffset();
          if (o == null) { if (++recomecos > 1) throw new Error('O envio foi perdido no servidor. Escolha o arquivo de novo.'); await abrirNovo(); } else offset = o;
          amostras.length = 0;
          mudar({ fase: 'enviando', enviados: offset });
          continue;
        }
        throw e;
      }
      if (r.status === 200) {
        offset = Number(r.j.offset);
        falhas = 0;
        medir(offset);
        if (atual?.fase !== 'enviando') mudar({ fase: 'enviando' });
        continue;
      }
      if (r.status === 409 && r.j?.offset != null) {
        // Outro pedaço ainda chegando ou offset desencontrado: segue do que o servidor tem.
        if (r.j.motivo === 'ocupado') await pausa(1500);
        offset = Number(r.j.offset);
        continue;
      }
      if (r.status === 404 && r.j?.motivo === 'envio_nao_encontrado') {
        if (++recomecos > 1) throw new Error('O envio foi perdido no servidor. Escolha o arquivo de novo.');
        await abrirNovo();
        continue;
      }
      if (transitorio(r.status)) {
        await respirar();
        const o = await perguntarOffset();
        if (o == null) { if (++recomecos > 1) throw new Error('O envio foi perdido no servidor. Escolha o arquivo de novo.'); await abrirNovo(); } else offset = o;
        mudar({ fase: 'enviando' });
        continue;
      }
      throw new Error(r.j?.error || `O servidor recusou o vídeo (${r.status}).`);
    }

    mudar({ fase: 'concluindo', enviados: arquivo.size, velocidade: 0 });
    for (;;) {
      checar();
      let r;
      try { r = await pedir('POST', `${base}/concluido`, { upload_id: uploadId }); } catch (e) {
        if (e instanceof ErroDeRede) { await respirar(); continue; }
        throw e;
      }
      if (r.status === 202) break;
      if (transitorio(r.status)) { await respirar(); continue; }
      throw new Error(r.j?.error || `Não deu para concluir o envio (${r.status}).`);
    }
    mudar({ fase: 'concluido' });
    return true;
  } catch (e: any) {
    if (e instanceof Cancelado) {
      if (uploadId) await pedir('DELETE', `${base}/${uploadId}`).catch(() => {});
      mudar({ fase: 'cancelado', velocidade: 0 });
    } else {
      mudar({ fase: 'erro', velocidade: 0, erro: e?.message || 'O envio falhou.' });
    }
    return false;
  } finally {
    meu.xhr = null;
    window.removeEventListener('beforeunload', segurarAba);
  }
}

// "1,24 GB", "412 MB"
export function tamanhoLegivel(b: number): string {
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} GB`;
  return `${Math.round(b / 1024 ** 2).toLocaleString('pt-BR')} MB`;
}
// "faltam 3 min 20 s"
export function tempoLegivel(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return '';
  s = Math.ceil(s);
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  if (h) return `${h} h ${m} min`;
  return r && m < 10 ? `${m} min ${r} s` : `${m} min`;
}
